import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { MessageCircle, X, Send, Sparkles, Minimize2 } from 'lucide-react';

export default function ChatbotWidget() {
    const { user } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    if (!user) return null;
    // Hide global chatbot on credit-repair page (has its own AI Coach)
    if (location.pathname.startsWith('/credit-repair')) return null;

    const sendMessage = async () => {
        if (!input.trim() || loading) return;
        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setLoading(true);
        try {
            const { data } = await API.post('/chatbot/message', {
                message: userMsg,
                session_id: sessionId,
            });
            setSessionId(data.session_id);
            setMessages(prev => [...prev, { role: 'ai', content: data.response }]);
        } catch (err) {
            setMessages(prev => [...prev, { role: 'ai', content: 'Sorry, I encountered an error. Please try again.' }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Floating Button */}
            {!open && (
                <button onClick={() => setOpen(true)} data-testid="chatbot-toggle"
                    className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-emerald-700 hover:bg-emerald-600 text-white shadow-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95">
                    <MessageCircle className="w-6 h-6" />
                </button>
            )}

            {/* Chat Panel */}
            {open && (
                <div className="fixed bottom-6 right-6 z-50 w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden animate-fade-in-up" data-testid="chatbot-panel">
                    {/* Header */}
                    <div className="bg-emerald-700 px-4 py-3 flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-emerald-200" />
                            <span className="text-sm font-semibold text-white font-['Outfit']">VisionAire AI</span>
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setOpen(false)} className="text-emerald-200 hover:text-white p-1">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Messages */}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
                        {messages.length === 0 && (
                            <div className="text-center py-8">
                                <Sparkles className="w-8 h-8 text-emerald-200 mx-auto mb-3" />
                                <p className="text-sm text-zinc-500 mb-3">Hi {user.name?.split(' ')[0] || 'there'}! How can I help?</p>
                                <div className="space-y-1.5">
                                    {['How do I improve my credit score?', 'What are my deal options?', 'Help me with disputes'].map(q => (
                                        <button key={q} onClick={() => { setInput(q); }}
                                            className="block w-full text-left text-xs px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition-colors">
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-emerald-700 text-white rounded-br-sm' : 'bg-zinc-100 text-zinc-800 rounded-bl-sm'}`}>
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start">
                                <div className="bg-zinc-100 rounded-2xl rounded-bl-sm px-4 py-3">
                                    <div className="flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" />
                                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '0.15s' }} />
                                        <div className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '0.3s' }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Input */}
                    <div className="p-3 border-t border-zinc-200 shrink-0">
                        <div className="flex gap-2">
                            <input value={input} onChange={e => setInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
                                placeholder="Ask anything..."
                                className="flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300"
                                data-testid="chatbot-input" />
                            <button onClick={sendMessage} disabled={loading || !input.trim()}
                                className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-40 transition-colors"
                                data-testid="chatbot-send">
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
